import { useState, useEffect } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../Label";
import Input from "../input/InputField";
import Select from "../Select";
import TextArea from "../input/TextArea";
import { useDropzone } from "react-dropzone";
import { Mail, Phone, Trash2Icon, AlertCircle } from "lucide-react";
import Switch from "../switch/Switch";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";
import Modal from "../../common/Modal";
import ImageCropModal from "../../common/ImageCropModal";
import { storageService } from "../../../services/storageService";

interface ProductFormData {
    name: string;
    description: string;
    category: string;
    type: string;
    companyId: string;
    pageUrl: string;
    imageUrl: string;
    supportInfo: {
        email: string;
        phone: string;
        refundPeriod: string;
        invoiceName: string;
    };
    status: string;
    imageKey?: string;
    customThankYouPageActive?: boolean;
    customThankYouPageUrl?: string;
    enableCoupon?: boolean;
}

interface EditProductFormProps {
    productId?: string; // ID do produto a ser editado
    onSuccess: (data: any) => void;
    onError: (error: any) => void;
}

// Função para aplicar máscara de telefone: (00) 00000-0000
const applyMaskPhone = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2')
        .substr(0, 15);
};

export default function EditProductForm({ productId, onSuccess, onError }: EditProductFormProps) {
    const [formData, setFormData] = useState<ProductFormData>({
        name: "",
        description: "",
        category: "",
        type: "",
        companyId: authService.getCompanyId() || '',
        pageUrl: "",
        imageUrl: "",
        supportInfo: {
            email: "",
            phone: "",
            refundPeriod: "",
            invoiceName: ""
        },
        status: "active",
        imageKey: "",
        customThankYouPageActive: false,
        customThankYouPageUrl: "",
        enableCoupon: false
    });

    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [image, setImage] = useState<File | null>(null);
    const [apiError, setApiError] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState<string>("");
    const [imageHover, setImageHover] = useState(false);
    const [tempImage, setTempImage] = useState<string>("");

    // Buscar o ID da empresa ao carregar o componente
    useEffect(() => {
        const user = authService.getUser();
        // Supondo que o usuário tenha um campo companyId na resposta de login
        if (user && user.companyId) {
            setFormData(prev => ({
                ...prev,
                companyId: user.companyId
            }));
        }
    }, []);

    // Buscar dados do produto quando o componente é montado e quando productId muda
    useEffect(() => {
        const fetchProductData = async () => {
            if (!productId) return;

            setFetchLoading(true);
            setApiError("");

            try {
                const response = await axios.get(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                // Acessar o objeto product dentro da resposta
                const productData = response.data.product;

                if (!productData) {
                    throw new Error("Dados do produto não encontrados na resposta");
                }

                setFormData({
                    name: productData.name || "",
                    description: productData.description || "",
                    category: productData.category || "",
                    type: productData.type || "",
                    companyId: productData.companyId || authService.getCompanyId() || '',
                    pageUrl: productData.pageUrl || "",
                    imageUrl: productData.imageUrl || "",
                    imageKey: productData.imageKey || "",
                    customThankYouPageActive: !!productData.customThankYouPageActive,
                    customThankYouPageUrl: productData.customThankYouPageUrl || "",
                    supportInfo: {
                        email: productData.supportInfo?.email || "",
                        phone: productData.supportInfo?.phone || "",
                        refundPeriod: productData.supportInfo?.refundPeriod || "",
                        invoiceName: productData.supportInfo?.invoiceName || ""
                    },
                    status: productData.status || "active",
                    enableCoupon: productData.enableCoupon || false
                });

            } catch (error: any) {
                console.error("Erro ao buscar dados do produto:", error);
                setApiError(error.response?.data?.message || "Erro ao carregar dados do produto. Tente novamente.");
            } finally {
                setFetchLoading(false);
            }
        };

        fetchProductData();
    }, [productId]);

    const productTypeOptions = [
        { value: "course", label: "Curso" },
        { value: "subscription", label: "Assinatura" },
        { value: "ebook", label: "E-book" },
        { value: "consultancy", label: "Consultoria" },
        { value: "mentoring", label: "Mentoria" },
        { value: "other", label: "Outro" },
    ];

    const productCategoryOptions = [
        { value: "animals", label: "Animais (Pets)" },
        { value: "phoneApps", label: "Aplicativos para celular" },
        { value: "architecture", label: "Arquitetura" },
        { value: "cinema", label: "Cinema" },
        { value: "concentration", label: "Concentração" },
        { value: "cooking", label: "Culinária e Gastronomia" },
        { value: "personalDevelopment", label: "Desenvolvimento pessoal" },
        { value: "educational", label: "Educacional" },
        { value: "weightLoss", label: "Emagrecimento" },
        { value: "engineering", label: "Engenharia" },
        { value: "entertainment", label: "Entretenimento" },
        { value: "finances", label: "Finanças" },
        { value: "general", label: "Geral" },
        { value: "hobby", label: "Hobbie" },
        { value: "languages", label: "Idiomas" },
        { value: "imagesIconsPhotos", label: "Imagens / ícones / fotos" },
        { value: "children", label: "Infantil" },
        { value: "internet", label: "Internet" },
        { value: "investments", label: "Investimentos" },
        { value: "games", label: "Jogos" },
        { value: "legal", label: "Jurídico" },
        { value: "networkMarketing", label: "Marketing de Rede" },
        { value: "digitalMarketing", label: "Marketing Digital" },
        { value: "studyMethod", label: "Método de Estudo" },
        { value: "fashionBeauty", label: "Moda / Beleza / Estética" },
        { value: "music", label: "Música" },
        { value: "plantsGardening", label: "Plantas / Flores / Jardinagem" },
        { value: "relationships", label: "Relacionamento" },
        { value: "religious", label: "Religioso" },
        { value: "health", label: "Saúde" },
        { value: "sexuality", label: "Sexualidade" },
        { value: "software", label: "Software" },
        { value: "technology", label: "Tecnologia" },
        { value: "templates", label: "Templates" },
        { value: "tourism", label: "Turismo" }
    ];

    const productRefundOptions = [
        { value: "7", label: "7 dias" },
        { value: "15", label: "15 dias" },
        { value: "30", label: "30 dias" },
    ];

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");

        // Mapeamento de IDs para estrutura do formData
        switch (id) {
            case "inputName":
                setFormData({ ...formData, name: value });
                break;
            case "inputUrl":
                setFormData({ ...formData, pageUrl: value });
                break;
            case "inputInvoiceName":
                setFormData({
                    ...formData,
                    supportInfo: { ...formData.supportInfo, invoiceName: value }
                });
                break;
            default:
                break;
        }
    };

    const handleTextAreaChange = (value: string) => {
        setFormData({ ...formData, description: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.description && value) {
            setErrors({
                ...errors,
                description: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleTypeChange = (value: string) => {
        setFormData({ ...formData, type: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.type && value) {
            setErrors({
                ...errors,
                type: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleCategoryChange = (value: string) => {
        setFormData({ ...formData, category: value });

        // Limpar o erro se o campo foi preenchido
        if (errors.category && value) {
            setErrors({
                ...errors,
                category: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleRefundPeriodChange = (value: string) => {
        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, refundPeriod: value }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setImage(file);

            // Criar URL para visualização e recorte da imagem
            const imageUrl = URL.createObjectURL(file);
            setCropImageSrc(imageUrl);
            setShowCropModal(true);
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleCropComplete = async (croppedImageDataUrl: string) => {
        setShowCropModal(false);

        try {
            // Converter Data URL para Blob
            const response = await fetch(croppedImageDataUrl);
            const blob = await response.blob();

            // Criar um arquivo a partir do blob com um nome significativo
            const fileName = image?.name || `product-image-${Date.now()}.jpg`;
            const croppedFile = new File([blob], fileName, { type: 'image/jpeg' });

            // Apenas armazenar a imagem recortada para upload posterior
            setImage(croppedFile);
            setTempImage(croppedImageDataUrl); // Para visualização

        } catch (error: any) {
            console.error("Erro ao processar imagem:", error);
            setApiError("Falha ao processar imagem. Por favor, tente novamente.");
        }
    };

    const handleRemoveImage = () => {
        if (formData.imageKey) {
            storageService.deleteFile(formData.imageKey)
                .then(() => {
                    setFormData({
                        ...formData,
                        imageUrl: "",
                        imageKey: ""
                    });
                })
                .catch(error => {
                    console.error("Erro ao excluir imagem:", error);
                    setApiError("Falha ao excluir imagem. Por favor, tente novamente.");
                });
        } else {
            // Se não tiver imageKey, apenas limpa os estados locais
            setFormData({
                ...formData,
                imageUrl: "",
                imageKey: ""
            });
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/png": [],
            "image/jpeg": [],
            "image/webp": [],
            "image/svg+xml": [],
            "image/gif": [],
        },
        maxSize: 2 * 1024 * 1024, // 2MB
    });

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(email);

        if (!isValidEmail && email.trim() !== '') {
            setErrors({
                ...errors,
                email: "Este é um endereço de email inválido."
            });
        } else {
            const newErrors = { ...errors };
            delete newErrors.email;
            setErrors(newErrors);
        }

        return isValidEmail;
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, email: value }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    // Validação de email no blur para melhor experiência do usuário
    const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        validateEmail(e.target.value);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const maskedValue = applyMaskPhone(value);

        setFormData({
            ...formData,
            supportInfo: { ...formData.supportInfo, phone: maskedValue }
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name) {
            newErrors.inputName = "O nome do produto é obrigatório";
        }

        if (!formData.description) {
            newErrors.description = "A descrição do produto é obrigatória";
        }

        if (!formData.type) {
            newErrors.type = "O tipo do produto é obrigatório";
        }

        if (!formData.category) {
            newErrors.category = "A categoria do produto é obrigatória";
        }

        if (!formData.supportInfo.email) {
            newErrors.email = "O e-mail de suporte é obrigatório";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supportInfo.email)) {
            newErrors.email = "O e-mail informado é inválido";
        }

        setErrors(newErrors);

        // Se não houver erros, retorna true
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }

        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");

        try {
            // Upload da imagem se houver
            let imageUrl = formData.imageUrl;
            let imageKey = formData.imageKey;

            if (image) {
                setUploadLoading(true);
                // Faz o upload da imagem recortada
                const uploadResult = await storageService.uploadFile(
                    image,
                    'products',
                    undefined,
                    formData.imageKey // Passa a chave antiga para ser excluída
                );

                if (uploadResult.success && uploadResult.url && uploadResult.key) {
                    imageUrl = uploadResult.url;
                    imageKey = uploadResult.key;
                } else {
                    throw new Error(uploadResult.error || 'Falha ao fazer upload da imagem');
                }
                setUploadLoading(false);
            }

            // Preparar dados para API
            const productData = {
                name: formData.name,
                description: formData.description,
                category: formData.category,
                type: formData.type,
                companyId: formData.companyId,
                pageUrl: formData.pageUrl,
                imageUrl: imageUrl,
                imageKey: imageKey,
                supportInfo: formData.supportInfo,
                status: formData.status,
                customThankYouPageActive: formData.customThankYouPageActive,
                customThankYouPageUrl: formData.customThankYouPageUrl,
                enableCoupon: formData.enableCoupon
            };

            let response;

            if (productId) {
                // Atualizar produto existente
                response = await axios.put(
                    `${import.meta.env.VITE_API_URL}/api/products/${productId}`,
                    productData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 200) {
                    // Chamar o callback de sucesso
                    onSuccess(response.data);
                }
            } else {
                // Criar novo produto
                response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/api/products/create`,
                    productData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    }
                );

                if (response.status === 201) {
                    // Chamar o callback de sucesso
                    onSuccess(response.data);
                }
            }
        } catch (error: any) {
            console.error("Erro ao salvar produto:", error);
            setApiError(error.response?.data?.message || "Erro ao salvar o produto. Tente novamente.");

            // Chamar o callback de erro
            onError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            status: checked ? "active" : "inactive"
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleCouponSwitchChange = (checked: boolean) => {
        setFormData({
            ...formData,
            enableCoupon: checked
        });

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
    };

    const handleDeleteProduct = async () => {
        if (!productId) return;

        setDeleteLoading(true);
        setApiError("");

        try {
            // Chamada para a API para excluir o produto
            const response = await axios.delete(
                `${import.meta.env.VITE_API_URL}/api/products/${productId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.status === 200) {
                // Chamar o callback de sucesso
                onSuccess({ message: "Produto excluído com sucesso" });
            }
        } catch (error: any) {
            console.error("Erro ao excluir produto:", error);
            setApiError(error.response?.data?.message || "Erro ao excluir o produto. Tente novamente.");

            // Chamar o callback de erro
            onError(error);
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Informações Básicas">
                    {fetchLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <>
                            {apiError && (
                                <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4">
                                    {apiError}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div>
                                    <Switch
                                        label="Vendas ativas"
                                        checked={formData.status === "active"}
                                        onChange={handleSwitchChange}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Switch
                                        label="Ativar cupom de desconto"
                                        checked={formData.enableCoupon || false}
                                        onChange={handleCouponSwitchChange}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputName">Nome do produto *</Label>
                                    <Input
                                        type="text"
                                        id="inputName"
                                        placeholder="Informe o nome do seu produto"
                                        onChange={handleInputChange}
                                        value={formData.name}
                                        error={!!errors.inputName}
                                        hint={errors.inputName}
                                    />
                                </div>

                                <div className="mt-5">
                                    <Label htmlFor="inputUrl">Url da página de vendas</Label>
                                    <Input
                                        type="text"
                                        id="inputUrl"
                                        placeholder="Ex.: https://minhapagina.com.br"
                                        onChange={handleInputChange}
                                        value={formData.pageUrl}
                                    />
                                </div>

                                <div className="mt-8 grid grid-cols-1 md:grid-cols-1 gap-4">
                                    <Switch
                                        label="Ativar página de obrigado personalizada?"
                                        checked={formData.customThankYouPageActive}
                                        onChange={checked =>
                                            setFormData({ ...formData, customThankYouPageActive: checked })
                                        }
                                    />
                                </div>

                                {formData.customThankYouPageActive && (
                                    <div className="mt-5">
                                        <Label htmlFor="customThankYouPageUrl">URL da página de obrigado personalizada *</Label>
                                        <Input
                                            type="text"
                                            id="customThankYouPageUrl"
                                            placeholder="URL da página de obrigado personalizada"
                                            value={formData.customThankYouPageUrl}
                                            onChange={e =>
                                                setFormData({ ...formData, customThankYouPageUrl: e.target.value })
                                            }
                                            error={!!errors.customThankYouPageUrl}
                                            hint={errors.customThankYouPageUrl}
                                        />
                                    </div>
                                )}


                                <div className="mt-5">
                                    <Label htmlFor="inputDescription">Descrição do produto *</Label>
                                    <TextArea
                                        value={formData.description}
                                        onChange={handleTextAreaChange}
                                        rows={6}
                                        placeholder="Informe do que ele se trata e o que oferece de forma clara e breve."
                                        error={!!errors.description}
                                        hint={errors.description}
                                    />
                                </div>

                                <Label htmlFor="inputImage" className="mt-5 block">Imagem de capa do produto</Label>

                                <div className="transition border border-gray-300 border-dashed cursor-pointer dark:hover:border-brand-500 dark:border-gray-700 rounded-xl hover:border-brand-500">
                                    {tempImage || formData.imageUrl ? (
                                        <div
                                            className="relative rounded-xl overflow-hidden"
                                            onMouseEnter={() => setImageHover(true)}
                                            onMouseLeave={() => setImageHover(false)}
                                        >
                                            <img
                                                src={tempImage || formData.imageUrl}
                                                alt="Imagem do produto"
                                                className="w-full h-64 object-contain"
                                            />
                                            {imageHover && (
                                                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={handleRemoveImage}
                                                        className="p-2 bg-red-500 text-white rounded-full"
                                                    >
                                                        <Trash2Icon size={24} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            {...getRootProps()}
                                            className={`dropzone rounded-xl border-dashed border-gray-300 p-7 lg:p-10
                                            ${isDragActive
                                                    ? "border-brand-500 bg-gray-100 dark:bg-gray-800"
                                                    : "border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
                                                }`}
                                            id="inputImage"
                                        >
                                            {/* Hidden Input */}
                                            <input {...getInputProps()} />

                                            <div className="dz-message flex flex-col items-center m-0!">
                                                {/* Icon Container */}
                                                <div className="mb-[22px] flex justify-center">
                                                    {uploadLoading ? (
                                                        <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                                            <svg
                                                                className="fill-current"
                                                                width="29"
                                                                height="28"
                                                                viewBox="0 0 29 28"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    clipRule="evenodd"
                                                                    d="M14.5019 3.91699C14.2852 3.91699 14.0899 4.00891 13.953 4.15589L8.57363 9.53186C8.28065 9.82466 8.2805 10.2995 8.5733 10.5925C8.8661 10.8855 9.34097 10.8857 9.63396 10.5929L13.7519 6.47752V18.667C13.7519 19.0812 14.0877 19.417 14.5019 19.417C14.9161 19.417 15.2519 19.0812 15.2519 18.667V6.48234L19.3653 10.5929C19.6583 10.8857 20.1332 10.8855 20.426 10.5925C20.7188 10.2995 20.7186 9.82463 20.4256 9.53184L15.0838 4.19378C14.9463 4.02488 14.7367 3.91699 14.5019 3.91699ZM5.91626 18.667C5.91626 18.2528 5.58047 17.917 5.16626 17.917C4.75205 17.917 4.41626 18.2528 4.41626 18.667V21.8337C4.41626 23.0763 5.42362 24.0837 6.66626 24.0837H22.3339C23.5766 24.0837 24.5839 23.0763 24.5839 21.8337V18.667C24.5839 18.2528 24.2482 17.917 23.8339 17.917C23.4197 17.917 23.0839 18.2528 23.0839 18.667V21.8337C23.0839 22.2479 22.7482 22.5837 22.3339 22.5837H6.66626C6.25205 22.5837 5.91626 22.2479 5.91626 21.8337V18.667Z"
                                                                />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Text Content */}
                                                <h4 className="mb-3 font-semibold text-gray-800 text-theme-xl dark:text-white/90">
                                                    {uploadLoading
                                                        ? "Enviando..."
                                                        : isDragActive
                                                            ? "Drop Files Here"
                                                            : "Arraste um arquivo"}
                                                </h4>

                                                <span className="text-center mb-3 block w-full max-w-[290px] text-sm text-gray-700 dark:text-gray-400">
                                                    {uploadLoading
                                                        ? "Aguarde enquanto enviamos a imagem"
                                                        : image
                                                            ? image.name
                                                            : "Arraste e solte arquivos JPG, JPEG, PNG, WebP, GIF, de no máximo 2MB, ou"}
                                                </span>

                                                {!uploadLoading && (
                                                    <span className="font-medium underline text-theme-sm text-brand-500">
                                                        Clique para procurar...
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 mt-5">
                                    <div>
                                        <Label>Tipo de produto *</Label>
                                        <Select
                                            options={productTypeOptions}
                                            placeholder="Selecione uma opção"
                                            onChange={handleTypeChange}
                                            value={formData.type}
                                            readOnly={true}
                                            className={`dark:bg-dark-900 ${errors.type ? 'border-red-500' : ''}`}
                                        />
                                        {errors.type && <p className="mt-1 text-sm text-red-500">{errors.type}</p>}
                                    </div>

                                    <div>
                                        <Label>Categoria do produto *</Label>
                                        <Select
                                            options={productCategoryOptions}
                                            placeholder="Selecione uma opção"
                                            onChange={handleCategoryChange}
                                            value={formData.category}
                                            className={`dark:bg-dark-900 ${errors.category ? 'border-red-500' : ''}`}
                                        />
                                        {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}
                                    </div>

                                    <div>
                                        <Label>Prazo de reembolso</Label>
                                        <Select
                                            options={productRefundOptions}
                                            placeholder="Selecione uma opção"
                                            onChange={handleRefundPeriodChange}
                                            value={formData.supportInfo.refundPeriod}
                                            className="dark:bg-dark-900"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="inputInvoiceName">Nome na fatura</Label>
                                        <Input
                                            type="text"
                                            id="inputInvoiceName"
                                            placeholder="Informe o nome que irá aparecer na fatura do cartão"
                                            onChange={handleInputChange}
                                            value={formData.supportInfo.invoiceName}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="email">E-mail de contato do suporte</Label>
                                        <div className="relative">
                                            <Input
                                                placeholder="E-mail de contato do suporte"
                                                type="email"
                                                id="email"
                                                className={`pl-[62px] ${errors.email ? 'border-red-500' : ''}`}
                                                value={formData.supportInfo.email}
                                                onChange={handleEmailChange}
                                                onBlur={handleEmailBlur}
                                            />
                                            <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                                <Mail className="size-4" />
                                            </span>
                                        </div>
                                        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <Label>Telefone de contato do suporte:</Label>
                                        <div className="relative">
                                            <Input
                                                placeholder="(99) 99999-9999"
                                                type="text"
                                                id="phone"
                                                value={formData.supportInfo.phone}
                                                onChange={handlePhoneChange}
                                                className="pl-[62px]"
                                            />
                                            <span className="absolute left-0 top-1/2 -translate-y-1/2 border-r border-gray-200 px-3.5 py-3 text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                                <Phone className="size-4" />
                                            </span>
                                            {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 flex justify-between gap-4.5">
                                    <Button
                                        variant="outline"
                                        size="md"
                                        startIcon={<Trash2Icon />}
                                        className="text-red-500 dark:text-red-400"
                                        onClick={() => {
                                            setApiError(""); // Limpar mensagem de erro ao abrir o modal
                                            setShowDeleteModal(true);
                                        }}
                                    >
                                        Excluir Produto
                                    </Button>
                                    <Button
                                        size="md"
                                        variant="primary"
                                        onClick={() => handleSubmit()}
                                        disabled={loading}
                                    >
                                        {loading ? "Salvando..." : "Salvar"}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </ComponentCard >
            </div>

            {/* Modal de confirmação para excluir produto */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                size="sm"
                onConfirm={handleDeleteProduct}
                confirmText={deleteLoading ? "Excluindo..." : "Excluir"}
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir este produto?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Excluindo este produto, as integrações vinculadas a ele e todos os dados criados serão perdidos.
                    </p>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>

            {showCropModal && (
                <ImageCropModal
                    imageSrc={cropImageSrc}
                    open={showCropModal}
                    onClose={() => setShowCropModal(false)}
                    onCropComplete={(croppedImage) => handleCropComplete(croppedImage)}
                />
            )}
        </div>
    );
}
